import _ from 'lodash';
import reporter from '../reporter';
import {generateError} from './errors';
import colorKeywords from './colorKeywords';
import constants from '../constants';

const colorTypes = {
    RGBA: {
        type: 'rgbaColor',
        isValid: isValidRgbOrRgba,
        exampleFormats: '"red", "#FF0000", "#FF000000", "rgb(225, 0, 0)" or "rgba(225, 0, 0, 0)"'
    },
    RGB: {
        type: 'rgbColor',
        isValid: isValidRgb,
        exampleFormats: '"red", "#FF0000", or "rgb(225, 0, 0)"'
    }
};

const HexadecimalLengths = {
    HEXA: {LONG: 9, SHORT: 5},
    HEX: {LONG: 7, SHORT: 4}
};

const STYLE_REFERENCE_BASE_URL = `${constants.WIX_CODE_REFERENCE_BASE_URL}$w.Style.html#`;

function extractNumericalValues(string) {
    return string.replace(/[^\d,]/g, '').split(',');
}

function isHexColor(colorToValidate) {
    const validHexColor = /^#(([0-9|a-f|A-F]){3}){1,2}$/;
    return validHexColor.test(colorToValidate);
}

function isHexaColor(colorToValidate) {
    const validHexaColor = /^#([a-f0-9]{8}|[a-f0-9]{4})\b$/gi;
    return validHexaColor.test(colorToValidate);
}

function isRgbaColor(colorToValidate) {
    /*from : https://eddyerburgh.me/validate-rgb-or-rgba-value-in-javascript*/
    const rxValidRgba = /\b([R][G][B][A][(]\s*([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5])\s*,\s*([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5])\s*,\s*([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5])(\s*,\s*((0\.[0-9]{1})|(1\.0)|(1)|(0)))?[)])/i;
    return rxValidRgba.test(colorToValidate);
}

function isRgbColor(colorToValidate) {
    const rxValidRgb = /\b([R][G][B][(]\s*([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5])\s*,\s*([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5])\s*,\s*([01]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5])(\s*)[)])/i;
    return rxValidRgb.test(colorToValidate);
}

function isKeyword(colorToValidate) {
    return _.get(colorKeywords, colorToValidate);
}

function isValidRgbOrRgba(colorToValidate) {
    return isValidRgba(colorToValidate) || isValidRgb(colorToValidate);
}

function isValidRgba(colorToValidate) {
    return isRgbaColor(colorToValidate) || isHexaColor(colorToValidate);
}

function isValidRgb(colorToValidate) {
    return isRgbColor(colorToValidate) || isHexColor(colorToValidate) || isKeyword(colorToValidate);
}

function rgbaToRgbCast(color) {
    if (isHexaColor(color)) {
        if (color.length === HexadecimalLengths.HEXA.LONG) {
            return color.slice(0, HexadecimalLengths.HEX.LONG);
        }

        if (color.length === HexadecimalLengths.HEXA.SHORT) {
            return color.slice(0, HexadecimalLengths.HEX.SHORT);
        }
    }

    if (isRgbaColor(color)) {
        const [red, green, blue] = extractNumericalValues(color);
        return `rgb(${red}, ${green}, ${blue})`;
    }
}

function validateColorAndPerformCastingIfNecessary(colorToValidate, opacitySupport, propertyName, compName) {
    const colorType = opacitySupport ? colorTypes.RGBA : colorTypes.RGB;
    if (colorType.isValid(colorToValidate)) {
        return colorToValidate;
    }

    if (!opacitySupport && isValidRgba(colorToValidate)) {
        reporter.reportSdkWarning(generateError.warning_color_casting_performed({
            propertyName,
            compName
        }));
        return rgbaToRgbCast(colorToValidate);
    }

    reporter.reportSdkError(generateError.error_invalid_css_value_multiple_expected_formats({
        propertyName,
        compName,
        cssProperty: colorType.type,
        exampleFormats: colorType.exampleFormats,
        infoLink: STYLE_REFERENCE_BASE_URL + propertyName
    }));
    return false;
}

function validatePixel(value, propertyName, compName, cssProperty) {
    let res = false;

    if (_.isString(value)) {
        const endsWithPx = _.endsWith(value, 'px');
        const integerToValidate = value.slice(0, value.length - 2);
        const containsOnlyNumbers = (/^\d*$/).test(integerToValidate);
        res = endsWithPx && containsOnlyNumbers && _.parseInt(integerToValidate);
    }

    if (res === false) {
        reporter.reportSdkError(generateError.error_invalid_css_value({
            propertyName,
            compName,
            cssProperty,
            exampleFormat: '1px',
            infoLink: STYLE_REFERENCE_BASE_URL + propertyName
        }));
    }

    return res;
}

export {
    validateColorAndPerformCastingIfNecessary,
    validatePixel
};



// WEBPACK FOOTER //
// ./js/modules/privates/validations/styleValidation.es6